import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import TextBundle from "aldoo-ra/CMS/text-bundle";
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import {
  getOrg,
  listOrgs,
  removeMember,
  promoteMember,
  demoteMember,
  removeInvite,
  resendInvite,
  orgLogoUrl,
  listTeams,
  createTeam,
  deleteTeam,
  renameTeam,
  setTeamMembers,
} from "./api";
import { updateQueryParams } from "aldoo-ra/SearchParams";
import { PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import CreateOrgDialog from "./CreateOrgDialog";
import InviteUsersDialog from "./InviteUsersDialog";
import DeleteOrgDialog from "./DeleteOrgDialog";

// ─── Avatar ──────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "#e53935",
  "#d81b60",
  "#8e24aa",
  "#3949ab",
  "#1e88e5",
  "#00897b",
  "#43a047",
  "#f4511e",
  "#fb8c00",
  "#6d4c41",
  "#546e7a",
  "#00acc1",
];

function hashColor(str) {
  if (!str) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function MemberAvatar({ member, size = 36 }) {
  if (member.avatar) {
    return (
      <img
        src={member.avatar}
        alt={member.name}
        className="rounded-full object-cover flex-shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  const initial = (member.name || "?")[0].toUpperCase();
  const bg = hashColor(member.userId);
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 select-none"
      style={{
        width: size,
        height: size,
        background: bg,
        fontSize: size * 0.4,
      }}
    >
      {initial}
    </div>
  );
}

// ─── Member row ───────────────────────────────────────────────────────────────

function MemberRow({
  member,
  myRole,
  currentUserId,
  onRemove,
  onPromote,
  onDemote,
  totalAdmins,
}) {
  const [confirmRemove, setConfirmRemove] = useState(false);

  const texts = TextBundle({ id: "aldoo_ra_organizations" });
  if (!texts) return null;

  const isAdmin = member.role === "admin";
  const isSelf = member.isSelf || member.userId === currentUserId;
  const canAct = myRole === "admin" && !isSelf;
  const isLastAdmin = isAdmin && totalAdmins <= 1;

  return (
    <div className="flex items-center gap-3 py-3 px-1">
      <MemberAvatar member={member} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium text-text truncate">
            {member.name}
          </span>
          {isSelf && <span className="text-xs text-text_secondary">{texts.you_label}</span>}
        </div>
        {member.email && (
          <span className="text-xs text-text_secondary truncate">
            {member.email}
          </span>
        )}
      </div>

      {canAct ? (
        <button
          onClick={() =>
            isAdmin ? onDemote(member.userId) : onPromote(member.userId)
          }
          disabled={isLastAdmin}
          title={isAdmin ? texts.demote_to_member : texts.promote_to_admin}
          className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            isAdmin
              ? "bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"
              : "bg-canvas text-text_secondary border-bordered hover:bg-black/5"
          }`}
        >
          {isAdmin ? texts.role_admin : texts.role_member}
        </button>
      ) : (
        isAdmin && (
          <span className="flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
            {texts.role_admin}
          </span>
        )
      )}

      {canAct &&
        (confirmRemove ? (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => {
                setConfirmRemove(false);
                onRemove(member.userId);
              }}
              className="px-2.5 py-1 rounded-lg text-xs font-medium bg-error text-white hover:bg-error/80 transition-colors"
            >
              {texts.remove}
            </button>
            <button
              onClick={() => setConfirmRemove(false)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium text-text_secondary hover:bg-black/5 transition-colors"
            >
              {texts.cancel}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmRemove(true)}
            disabled={isLastAdmin}
            title={texts.remove_from_org_title}
            className="flex-shrink-0 p-1.5 rounded-lg text-text_secondary hover:text-error hover:bg-error/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M22 9H2M10 12v6M14 12v6M16 9l-1-4H9L8 9M5 9l1 12h12l1-12"
              />
            </svg>
          </button>
        ))}
    </div>
  );
}

// ─── Org list item ────────────────────────────────────────────────────────────

function OrgListItem({ org, onClick }) {
  const texts = TextBundle({ id: "aldoo_ra_organizations" });
  if (!texts) return null;

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-black/5 border border-bordered transition-colors text-left group"
    >
      <div className="w-10 h-10 rounded-xl overflow-hidden bg-canvas flex-shrink-0">
        {org.logo ? (
          <img
            src={orgLogoUrl(org._id)}
            alt={org.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-white text-sm font-bold"
            style={{ background: hashColor(org._id) }}
          >
            {org.name[0]?.toUpperCase()}
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text truncate">
          {org.name}
        </p>

        {org.role === "admin" && <p className="text-xs text-primary">
          {texts.role_admin}
        </p>}
      </div>

      <svg
        className="w-4 h-4 text-text_secondary group-hover:text-text transition-colors flex-shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path strokeLinecap="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

// ─── Add member overlay ───────────────────────────────────────────────────────

function AddMemberOverlay({ orgMembers, teamMemberIds, onAdd, onClose }) {
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  const texts = TextBundle({ id: "aldoo_ra_organizations" });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  if (!texts) return null;

  const available = orgMembers.filter((m) => {
    if (teamMemberIds.has(m.userId)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
    );
  });

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-paper rounded-2xl shadow-xl w-full max-w-sm mx-4 flex flex-col border border-bordered overflow-hidden">
        <div className="px-4 py-3 border-b border-bordered flex items-center gap-2">
          <svg
            className="w-4 h-4 text-text_secondary flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={texts.search_members_placeholder}
            className="flex-1 text-sm bg-transparent text-text placeholder-text_secondary focus:outline-none"
          />
          <button
            onClick={onClose}
            className="p-1 rounded text-text_secondary hover:text-text transition-colors"
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="overflow-y-auto max-h-72 divide-y divide-bordered">
          {available.length === 0 ? (
            <span className="block py-8 text-center text-sm text-text_secondary">
              {texts.no_members_to_add}
            </span>
          ) : (
            available.map((m) => (
              <button
                key={m.userId}
                onClick={() => onAdd(m)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-canvas transition-colors text-left"
              >
                <MemberAvatar member={m} size={32} />
                <div className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-text truncate">
                    {m.name}
                  </span>
                  {m.email && (
                    <span className="block text-xs text-text_secondary truncate">
                      {m.email}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Teams tab ────────────────────────────────────────────────────────────────

function TeamsTab({ orgId, orgMembers, isAdmin }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [newTeamName, setNewTeamName] = useState("");
  const [addingTeam, setAddingTeam] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clipboard, setClipboard] = useState(null);

  const texts = TextBundle({ id: "aldoo_ra_organizations" });

  const memberMap = Object.fromEntries(orgMembers.map((m) => [m.userId, m]));

  const load = useCallback(async () => {
    setLoading(true);
    const res = await listTeams(orgId);
    if (res.result) setTeams(res.teams);
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedTeam = teams.find((t) => t._id === selectedTeamId) || null;

  async function handleCreateTeam() {
    if (!newTeamName.trim()) return;
    const res = await createTeam(orgId, newTeamName.trim());
    if (!res.result) return;
    setTeams((prev) => [...prev, res.team]);
    setNewTeamName("");
    setAddingTeam(false);
    setSelectedTeamId(res.team._id);
  }

  async function handleDeleteTeam(teamId) {
    const res = await deleteTeam(orgId, teamId);
    if (!res.result) return;
    setTeams((prev) => prev.filter((t) => t._id !== teamId));
    if (selectedTeamId === teamId) setSelectedTeamId(null);
  }

  async function handleRename(teamId) {
    if (!renameValue.trim()) {
      setRenamingId(null);
      return;
    }
    const res = await renameTeam(orgId, teamId, renameValue.trim());
    if (res.result)
      setTeams((prev) =>
        prev.map((t) => (t._id === teamId ? { ...t, name: res.team.name } : t)),
      );
    setRenamingId(null);
  }

  async function handleSetMembers(teamId, members) {
    const res = await setTeamMembers(orgId, teamId, members);
    if (res.result)
      setTeams((prev) =>
        prev.map((t) =>
          t._id === teamId ? { ...t, members: res.team.members } : t,
        ),
      );
  }

  useEffect(() => {
    setConfirmClear(false);
  }, [selectedTeamId]);

  function handleAddMember(member) {
    if (!selectedTeam) return;
    handleSetMembers(selectedTeam._id, [
      ...selectedTeam.members,
      member.userId,
    ]);
    setShowAddMember(false);
  }

  function handleRemoveMember(userId) {
    if (!selectedTeam) return;
    handleSetMembers(
      selectedTeam._id,
      selectedTeam.members.filter((m) => m !== userId),
    );
  }

  if (!texts) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 min-h-[320px]">
      {/* Team list — hidden on mobile when a team is selected */}
      <div
        className={`md:w-48 md:flex-shrink-0 flex flex-col gap-2 ${selectedTeamId ? "hidden md:flex" : "flex"}`}
      >
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-text_secondary uppercase tracking-wide">
            {texts.teams_label}
          </span>
          {isAdmin && (
            <button
              onClick={() => setAddingTeam(true)}
              className="p-1 rounded text-text_secondary hover:text-primary transition-colors"
              title={texts.add_team_title}
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path strokeLinecap="round" d="M12 5v14M5 12h14" />
              </svg>
            </button>
          )}
        </div>

        {addingTeam && (
          <div className="flex gap-1">
            <input
              autoFocus
              type="text"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateTeam();
                if (e.key === "Escape") {
                  setAddingTeam(false);
                  setNewTeamName("");
                }
              }}
              onBlur={() => {
                setAddingTeam(false);
                setNewTeamName("");
              }}
              placeholder={texts.team_name_placeholder}
              className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-bordered rounded-lg bg-canvas text-text placeholder-text_secondary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleCreateTeam}
              className="px-2 py-1 rounded-lg bg-primary text-text_inv text-xs font-medium"
            >
              {texts.add}
            </button>
          </div>
        )}

        {teams.length === 0 && !addingTeam ? (
          <p className="text-xs text-text_secondary py-4 text-center">
            {texts.no_teams_yet}
          </p>
        ) : (
          <div className="flex flex-col gap-1">
            {teams.map((team) => (
              <div key={team._id}>
                {renamingId === team._id ? (
                  <input
                    autoFocus
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRename(team._id);
                      if (e.key === "Escape") setRenamingId(null);
                    }}
                    onBlur={() => handleRename(team._id)}
                    className="w-full text-xs px-2 py-1.5 border border-primary rounded-lg bg-canvas text-text focus:outline-none"
                  />
                ) : (
                  <button
                    onClick={() => setSelectedTeamId(team._id)}
                    className={`w-full flex items-center justify-between gap-1 px-3 py-2 rounded-xl text-xs font-medium text-left transition-colors ${
                      selectedTeamId === team._id
                        ? "bg-primary text-text_inv"
                        : "hover:bg-canvas text-text"
                    }`}
                  >
                    <span className="truncate">{team.name}</span>
                    <span
                      className={`text-[10px] rounded-full px-1.5 py-0.5 flex-shrink-0 ${
                        selectedTeamId === team._id
                          ? "bg-white/20"
                          : "bg-primary/10 text-primary"
                      }`}
                    >
                      {team.members.length}
                    </span>
                  </button>
                )}

                {isAdmin && selectedTeamId === team._id && (
                  <div className="flex gap-1 mt-1 px-1 flex-wrap">
                    <button
                      onClick={() => {
                        setRenamingId(team._id);
                        setRenameValue(team.name);
                      }}
                      className="text-[10px] text-text_secondary hover:text-text px-1.5 py-0.5 rounded transition-colors"
                    >
                      {texts.rename}
                    </button>
                    <button
                      onClick={() => setClipboard([...team.members])}
                      className="text-[10px] text-text_secondary hover:text-primary px-1.5 py-0.5 rounded transition-colors"
                    >
                      {texts.copy}
                    </button>
                    {clipboard && (
                      <button
                        onClick={() =>
                          handleSetMembers(team._id, [...clipboard])
                        }
                        className="text-[10px] text-primary font-medium px-1.5 py-0.5 rounded hover:bg-primary/10 transition-colors"
                      >
                        {texts.paste}
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteTeam(team._id)}
                      className="text-[10px] text-error hover:bg-error/10 px-1.5 py-0.5 rounded transition-colors ml-auto"
                    >
                      {texts.delete}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="hidden md:block w-px bg-bordered flex-shrink-0" />

      {/* Team detail */}
      <div
        className={`flex-1 min-w-0 flex flex-col ${selectedTeamId ? "flex" : "hidden md:flex"}`}
      >
        {!selectedTeam ? (
          <div className="flex items-center justify-center h-full py-12">
            <span className="text-sm text-text_secondary">
              {texts.select_team}
            </span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedTeamId(null)}
                  className="md:hidden p-1 rounded text-text_secondary hover:text-text transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm font-semibold text-text">
                  {selectedTeam.name}
                </span>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1">
                  {selectedTeam.members.length > 0 &&
                    (confirmClear ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            handleSetMembers(selectedTeam._id, []);
                            setConfirmClear(false);
                          }}
                          className="px-2.5 py-1 rounded-lg text-xs font-medium bg-error text-white hover:bg-error/80 transition-colors"
                        >
                          {texts.confirm}
                        </button>
                        <button
                          onClick={() => setConfirmClear(false)}
                          className="px-2.5 py-1 rounded-lg text-xs text-text_secondary hover:bg-black/5 transition-colors"
                        >
                          {texts.cancel}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmClear(true)}
                        className="px-2.5 py-1 rounded-lg text-xs text-error hover:bg-error/10 transition-colors"
                      >
                        {texts.clear_all}
                      </button>
                    ))}
                  <button
                    onClick={() => setShowAddMember(true)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary text-text_inv text-xs font-medium hover:bg-primary_dk transition-colors"
                  >
                    <svg
                      className="w-3 h-3"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path strokeLinecap="round" d="M12 5v14M5 12h14" />
                    </svg>
                    {texts.add_member}
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-col divide-y divide-bordered">
              {selectedTeam.members.length === 0 ? (
                <span className="block py-6 text-center text-sm text-text_secondary">
                  {texts.no_team_members}
                </span>
              ) : (
                selectedTeam.members.map((userId) => {
                  const m = memberMap[userId];
                  if (!m) return null;
                  return (
                    <div
                      key={userId}
                      className="flex items-center gap-3 py-2.5"
                    >
                      <MemberAvatar member={m} size={32} />
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-text truncate">
                          {m.name}
                        </span>
                        {m.email && (
                          <span className="block text-xs text-text_secondary truncate">
                            {m.email}
                          </span>
                        )}
                      </div>
                      {isAdmin && (
                        <button
                          onClick={() => handleRemoveMember(userId)}
                          className="p-1.5 rounded-lg text-text_secondary hover:text-error hover:bg-error/10 transition-colors flex-shrink-0"
                          title={texts.remove_from_team_title}
                        >
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {showAddMember && selectedTeam && (
        <AddMemberOverlay
          orgMembers={orgMembers}
          teamMemberIds={new Set(selectedTeam.members)}
          onAdd={handleAddMember}
          onClose={() => setShowAddMember(false)}
        />
      )}
    </div>
  );
}

// ─── Simple tab bar ───────────────────────────────────────────────────────────

function TabBar({ tabs, value, onChange }) {
  return (
    <div className="flex gap-1 border-b border-bordered mb-4">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          onClick={() => onChange(tab.value)}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            value === tab.value
              ? "border-primary text-primary"
              : "border-transparent text-text_secondary hover:text-text"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ─── Main panel ──────────────────────────────────────────────────────────────

export default function OrganizationsPanel({ currentUserId }) {
  const [orgs, setOrgs] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [deleteOrgTarget, setDeleteOrgTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [resendResultEmail, setResendResultEmail] = useState(null);
  const [orgTab, setOrgTab] = useState("members");

  const texts = TextBundle({ id: "aldoo_ra_organizations" });

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listOrgs();
      if (res.result) setOrgs(res.orgs);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrgDetail = useCallback(async (orgId) => {
    setLoadingOrg(true);
    try {
      const res = await getOrg(orgId);
      if (res.result) setOrgData(res);
    } finally {
      setLoadingOrg(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUserId) return;
    loadOrgs();
    const params = new URLSearchParams(window.location.search);
    const urlOrgId = params.get("orgId");
    if (urlOrgId) {
      setActiveOrgId(urlOrgId);
      loadOrgDetail(urlOrgId);
    }
  }, [currentUserId]); // eslint-disable-line

  function enterOrg(orgId) {
    setActiveOrgId(orgId);
    setOrgData(null);
    setSearch("");
    setOrgTab("members");
    updateQueryParams({ orgId });
    loadOrgDetail(orgId);
  }

  function exitOrg() {
    setActiveOrgId(null);
    setOrgData(null);
    setSearch("");
    updateQueryParams({ orgId: null });
  }

  async function handleRemove(userId) {
    const res = await removeMember(activeOrgId, userId);
    if (res.result) loadOrgDetail(activeOrgId);
  }

  async function handlePromote(userId) {
    const res = await promoteMember(activeOrgId, userId);
    if (res.result) loadOrgDetail(activeOrgId);
  }

  async function handleDemote(userId) {
    const res = await demoteMember(activeOrgId, userId);
    if (res.result) loadOrgDetail(activeOrgId);
  }

  async function handleInviteRemove(inviteId) {
    const res = await removeInvite(activeOrgId, inviteId);
    if (res.result) loadOrgDetail(activeOrgId);
  }

  async function handleInviteResend(inviteId, email) {
    const res = await resendInvite(activeOrgId, inviteId);
    if (!res.result) return;
    loadOrgDetail(activeOrgId);
    setResendResultEmail(email);
  }

  const filteredMembers = (orgData?.members ?? []).filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
    );
  });

  const adminCount = (orgData?.members ?? []).filter(
    (m) => m.role === "admin",
  ).length;
  const isAdmin = orgData?.myRole === "admin";

  if (!texts) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Detail view ──────────────────────────────────────────────────────────
  if (activeOrgId) {
    return (
      <div className="space-y-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={exitOrg}
            className="text-text_secondary hover:text-text transition-colors"
          >
            {texts.organizations}
          </button>
          <svg
            className="w-4 h-4 text-text_secondary flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path strokeLinecap="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-text font-medium truncate">
            {orgData?.org?.name ?? "…"}
          </span>
        </div>

        {loadingOrg ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : orgData ? (
          <div className="bg-paper rounded-2xl border border-bordered overflow-hidden">
            {/* Org header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-bordered">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl overflow-hidden bg-canvas flex-shrink-0">
                  {orgData.org.logo ? (
                    <img
                      src={orgLogoUrl(orgData.org._id)}
                      alt={orgData.org.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center text-white text-sm font-bold"
                      style={{ background: hashColor(orgData.org._id) }}
                    >
                      {orgData.org.name[0]?.toUpperCase()}
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-text text-sm">
                    {orgData.org.name}
                  </p>
                  <p className="text-xs text-text_secondary">
                    {orgData.members.length}{" "}
                    {orgData.members.length !== 1 ? texts.members : texts.member}
                  </p>
                  {isAdmin && (
                    <div className="flex items-center gap-1 mt-1">
                      <button
                        onClick={() => setEditOrg(orgData.org)}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-text_secondary hover:text-text hover:bg-black/5 transition-colors"
                      >
                        <svg
                          className="w-3 h-3"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"
                          />
                        </svg>
                        {texts.edit}
                      </button>
                      <button
                        onClick={() => setDeleteOrgTarget(orgData.org)}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-text_secondary hover:text-error hover:bg-error/10 transition-colors"
                      >
                        <svg
                          className="w-3 h-3"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M22 9H2M10 12v6M14 12v6M16 9l-1-4H9L8 9M5 9l1 12h12l1-12"
                          />
                        </svg>
                        {texts.delete}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {isAdmin && (orgTab === "members" || orgTab === "teams") && (
                <button
                  onClick={() => setShowInvite(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary hover:bg-primary_dk text-text_inv text-xs font-medium transition-colors flex-shrink-0"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path strokeLinecap="round" d="M12 5v14M5 12h14" />
                  </svg>
                  {texts.add_people}
                </button>
              )}
            </div>

            {/* Tabs + content */}
            <div className="px-5 pt-4">
              <TabBar
                tabs={[
                  { label: texts.tab_members, value: "members" },
                  { label: texts.teams_label, value: "teams" },
                ]}
                value={orgTab}
                onChange={setOrgTab}
              />

              {orgTab === "members" && (
                <>
                  <div className="mb-3 relative">
                    <svg
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text_secondary"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={texts.search_members_placeholder}
                      className="w-full pl-9 pr-3 py-2 text-sm bg-canvas border border-bordered rounded-lg text-text placeholder-text_secondary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div className="divide-y divide-bordered">
                    {filteredMembers.length === 0 ? (
                      <p className="py-8 text-center text-sm text-text_secondary">
                        {texts.no_members_found}
                      </p>
                    ) : (
                      filteredMembers.map((member) => (
                        <MemberRow
                          key={member.userId}
                          member={member}
                          myRole={orgData.myRole}
                          currentUserId={currentUserId}
                          onRemove={handleRemove}
                          onPromote={handlePromote}
                          onDemote={handleDemote}
                          totalAdmins={adminCount}
                        />
                      ))
                    )}
                  </div>

                  {isAdmin && orgData.invites?.length > 0 && (
                    <div className="border-t border-bordered mt-2 pt-3">
                      <p className="text-xs font-semibold text-text_secondary uppercase tracking-wider mb-2">
                        {texts.pending_invitations} ({orgData.invites.length})
                      </p>
                      <div className="divide-y divide-bordered">
                        {orgData.invites.map((inv) => (
                          <div
                            key={inv.inviteId}
                            className="flex items-center gap-3 py-3"
                          >
                            <div className="w-9 h-9 rounded-full bg-canvas flex-shrink-0 flex items-center justify-center">
                              <svg
                                className="w-4 h-4 text-text_secondary"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                              >
                                <path
                                  strokeLinecap="round"
                                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                                />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-text truncate">
                                {inv.email}
                              </p>
                              <span className="inline-flex items-center gap-1 text-xs text-warning font-medium mt-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-warning inline-block" />
                                {texts.pending}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() =>
                                  handleInviteResend(inv.inviteId, inv.email)
                                }
                                className="px-2.5 py-1 rounded-lg text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
                              >
                                {texts.resend}
                              </button>
                              <button
                                onClick={() => handleInviteRemove(inv.inviteId)}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium text-error hover:bg-error/10 transition-colors"
                              >
                                {texts.remove}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {orgTab === "teams" && (
                <TeamsTab
                  orgId={activeOrgId}
                  orgMembers={orgData.members}
                  isAdmin={isAdmin}
                />
              )}
            </div>

            <div className="pb-4" />
          </div>
        ) : null}

        {resendResultEmail &&
          createPortal(
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
              <div className="bg-paper rounded-2xl shadow-xl max-w-sm w-full mx-4 p-8 text-center space-y-4 border border-bordered">
                <div className="w-14 h-14 rounded-full bg-success/15 flex items-center justify-center mx-auto">
                  <svg
                    className="w-7 h-7 text-success"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-text">
                    {texts.invitation_sent_title}
                  </h3>
                  <p className="text-sm text-text_secondary">
                    {resendResultEmail}
                  </p>
                </div>
                <button
                  onClick={() => setResendResultEmail(null)}
                  className="w-full px-4 py-2 rounded-xl bg-primary hover:bg-primary_dk text-text_inv text-sm font-medium transition-colors"
                >
                  {texts.done}
                </button>
              </div>
            </div>,
            document.body,
          )}

        {editOrg && (
          <CreateOrgDialog
            org={editOrg}
            onClose={() => setEditOrg(null)}
            onSaved={() => {
              setEditOrg(null);
              loadOrgs();
              loadOrgDetail(activeOrgId);
            }}
          />
        )}
        {showInvite && (
          <InviteUsersDialog
            orgId={activeOrgId}
            onClose={() => {
              setShowInvite(false);
              loadOrgDetail(activeOrgId);
            }}
          />
        )}
        {deleteOrgTarget && (
          <DeleteOrgDialog
            org={deleteOrgTarget}
            onClose={() => setDeleteOrgTarget(null)}
            onDeleted={() => {
              setDeleteOrgTarget(null);
              exitOrg();
              loadOrgs();
            }}
          />
        )}
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Typography
          component="h2"
          variant="h4"
        >
          {texts.your_organizations}
        </Typography>

        <Button
          variant="primary"
          onClick={() => setShowCreate(true)}
          className="gap-1.5"
        >
          <PlusIcon className="w-4 h-4"/>
          {texts.new_organization}
        </Button>
      </div>

      {orgs.length === 0 ? (
        <div className="flex flex-col items-center bg-canvas rounded-2xl border border-dashed border-bordered p-10 text-center gap-3">
          <UserGroupIcon className="w-10 h-10 text-text_secondary"/>
          <p className="text-sm lg:text-base text-text_secondary">
            {texts.no_organizations_yet}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {orgs.map((org) => (
            <OrgListItem
              key={org._id}
              org={org}
              onClick={() => enterOrg(org._id)}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateOrgDialog
          onClose={() => setShowCreate(false)}
          onSaved={() => loadOrgs()}
        />
      )}
    </div>
  );
}